import { apiClient, unwrap } from './client';

/** PAIE-2 — paramètres légaux datés, rubriques, éléments de paie (docs/PLAN-PAIE.md). Montants en FCFA, taux en %. */

export interface TrancheIts { jusquaFcfa: number | null; tauxPct: number }
export interface ContenuReglesPaie {
  smigFcfa: number | null;
  cnps: {
    retraiteSalariePct: number | null; retraitePatronalePct: number | null; plafondRetraiteFcfa: number | null;
    prestationsFamilialesPct: number | null; maternitePct: number | null; accidentTravailPct: number | null; plafondPfAtFcfa: number | null;
  };
  cmu: { salarieFcfa: number | null; patronaleFcfa: number | null };
  its: { tranches: TrancheIts[]; reductionChargesFamille: { parts: number; montantFcfa: number }[] | null };
  patronales: { taxeApprentissagePct: number | null; formationContinuePct: number | null; contributionEmployeurItsPct: number | null };
  anciennete: { apresAnnees: number | null; tauxInitialPct: number | null; parAnneeSupplementairePct: number | null; plafondPct: number | null };
  transport: { exonereJusquaFcfa: number | null };
  // PAIE-3 : absents des versions plus anciennes (= vides)
  calculIts?: { abattementPct: number | null; deduireCotisationsSalariales: boolean | null };
  parts?: { celibataire: number | null; celibataireAvecEnfants?: number | null; marie: number | null; parEnfant: number | null; maximum: number | null };
  prorata?: { diviseurJours: number | null };
  sortie?: ParametresSortie;
}

/** PAIE-4 : paramètres du solde de tout compte (vides = à renseigner par le cabinet). */
export interface TraitementIndemnite { soumiseCnps: boolean | null; imposable: boolean | null }
export interface ParametresSortie {
  congesDiviseurJours: number | null;
  preavis: { categories: string[]; ancienneteMinAnnees: number; mois: number }[] | null;
  licenciementTranches: { jusquaAnnees: number | null; pct: number }[] | null;
  finCddPct: number | null;
  traitement: { conges: TraitementIndemnite; preavis: TraitementIndemnite; licenciement: TraitementIndemnite; finCdd: TraitementIndemnite } | null;
}

export interface VersionRegles {
  id: string; valableDu: string; contenu: ContenuReglesPaie; statut: 'a_confirmer' | 'confirmee';
  confirmePar: string | null; confirmeLe: string | null; sourceConfirmation: string | null;
  enVigueur: boolean; valeursVides: string[];
}

export interface Rubrique {
  id: string; code: string; libelle: string; sens: 'gain' | 'retenue'; soumiseCnps: boolean; imposable: boolean;
  calcul: 'saisie' | 'anciennete' | 'pointage_hs' | 'pointage_absence'; ordre: number; active: boolean; aConfirmer: boolean;
}

export interface ElementPaie {
  id: string; employeeId: string; rubriqueId: string; montantCents: string; du: string; au: string | null; commentaire: string | null;
  rubrique: { code: string; libelle: string; sens: 'gain' | 'retenue'; calcul: string };
}

export const paieApi = {
  regles:          async () => unwrap<VersionRegles[]>(await apiClient.get('/hr/paie/regles')),
  creerVersion:    async (valableDu: string, contenu: ContenuReglesPaie) => unwrap<VersionRegles[]>(await apiClient.post('/hr/paie/regles', { valableDu, contenu })),
  modifierVersion: async (id: string, contenu: ContenuReglesPaie) => unwrap<VersionRegles[]>(await apiClient.patch(`/hr/paie/regles/${id}`, { contenu })),
  confirmer:       async (id: string, source: string) => unwrap<VersionRegles[]>(await apiClient.post(`/hr/paie/regles/${id}/confirmer`, { source })),
  supprimer:       async (id: string) => unwrap<VersionRegles[]>(await apiClient.delete(`/hr/paie/regles/${id}`)),
  rubriques:       async () => unwrap<Rubrique[]>(await apiClient.get('/hr/paie/rubriques')),
  creerRubrique:   async (r: { code: string; libelle: string; sens: 'gain' | 'retenue'; soumiseCnps: boolean; imposable: boolean }) =>
    unwrap<Rubrique>(await apiClient.post('/hr/paie/rubriques', r)),
  modifierRubrique: async (id: string, r: Partial<Pick<Rubrique, 'libelle' | 'soumiseCnps' | 'imposable' | 'active' | 'aConfirmer'>>) =>
    unwrap<Rubrique>(await apiClient.patch(`/hr/paie/rubriques/${id}`, r)),
  elements:        async (employeeId: string) => unwrap<ElementPaie[]>(await apiClient.get('/hr/paie/elements', { params: { employeeId } })),
  creerElement:    async (e: { employeeId: string; rubriqueId: string; montantCents: number; du: string; au?: string | null; commentaire?: string }) =>
    unwrap<ElementPaie>(await apiClient.post('/hr/paie/elements', e)),
  terminerElement: async (id: string, au: string) => unwrap<ElementPaie>(await apiClient.post(`/hr/paie/elements/${id}/terminer`, { au })),
};

// ── PAIE-3 : paie du mois ─────────────────────────────────────────────────────────────────
export interface BulletinResume {
  id: string; employeeId: string; numero: string | null; nbAlertes: number; alertes: string[];
  employee: { fullName: string; matricule: string; department: string };
  brut?: number; cotisations?: number; impot?: number; net?: number; chargesPatronales?: number;
}
export interface PaieDuMois {
  mois: string;
  periode: null | { id: string; statut: 'brouillon' | 'validee'; essai: boolean; prepareLe: string; valideLe: string | null; validePar: string | null; rouvertLe: string | null; motifReouverture: string | null;
    comptabiliseLe?: string | null; ecriturePiece?: string | null; payeLe?: string | null; paiementPiece?: string | null; paiementReference?: string | null };
  bulletins: BulletinResume[];
  totaux: null | { brut: number; cotisations: number; impot: number; net: number; chargesPatronales: number; coutEmployeur: number; effectif: number };
  montantsVisibles: boolean;
  sansElements?: string[];
}

export const paieMoisApi = {
  lire:     async (mois: string) => unwrap<PaieDuMois>(await apiClient.get('/hr/paie/mois', { params: { mois } })),
  preparer: async (mois: string) => unwrap<PaieDuMois>(await apiClient.post('/hr/paie/mois/preparer', { mois })),
  valider:  async (mois: string) => unwrap<PaieDuMois>(await apiClient.post('/hr/paie/mois/valider', { mois })),
  rouvrir:  async (mois: string, motif: string) => unwrap<PaieDuMois>(await apiClient.post('/hr/paie/mois/rouvrir', { mois, motif })),
  telechargerPdf: async (id: string, nomParDefaut: string) => {
    const r = await apiClient.get(`/hr/paie/bulletins/${id}/pdf`, { responseType: 'blob' });
    const nom = String(r.headers['content-disposition'] ?? '').match(/filename="([^"]+)"/)?.[1] ?? nomParDefaut;
    const url = URL.createObjectURL(r.data);
    const lien = document.createElement('a');
    lien.href = url; lien.download = nom;
    document.body.appendChild(lien); lien.click(); lien.remove();
    URL.revokeObjectURL(url);
  },
};

// ── PAIE-4 : solde de tout compte ─────────────────────────────────────────────────────────
export type MotifSortie = 'demission' | 'licenciement' | 'fin_cdd' | 'retraite' | 'commun_accord' | 'deces';
export const LIBELLE_MOTIF_SORTIE: Record<MotifSortie, string> = {
  demission: 'Démission', licenciement: 'Licenciement', fin_cdd: 'Fin de CDD', retraite: 'Départ à la retraite',
  commun_accord: 'Rupture d’un commun accord', deces: 'Décès',
};
export interface SoldeToutCompte {
  id: string; employeeId: string; statut: 'brouillon' | 'valide'; essai: boolean; numero: string | null; motif: MotifSortie;
  dateSortie: string; valideLe: string | null; validePar: string | null; annuleLe: string | null; motifAnnulation: string | null; alertes: string[];
  resultat?: {
    salaireReferenceFcfa: number; salaireReferenceSource: string; ancienneteAnnees: number; joursCongesRestants: number;
    indemnites: { code: string; libelle: string; calcul: string; montantFcfa: number }[];
    bulletin: { brut: number; cotisationsSalariales: number; impot: number; net: number; lignes: { code: string; libelle: string; nature: string; montant: number }[] };
  };
}
export interface SaisieSolde {
  employeeId: string; motif: MotifSortie; preavisEffectue: boolean; fauteLourde?: boolean; joursCongesRestants: number;
  justificatifConges?: string; salaireReferenceFcfa?: number | null; justificationSalaire?: string; totalSalairesContratFcfa?: number | null;
  preavisMoisSaisis?: number | null; autres?: { libelle: string; montantFcfa: number; sens: 'gain' | 'retenue' }[];
}
export const soldeApi = {
  duSalarie: async (employeeId: string) => unwrap<SoldeToutCompte[]>(await apiClient.get('/hr/paie/soldes', { params: { employeeId } })),
  calculer:  async (s: SaisieSolde) => unwrap<SoldeToutCompte>(await apiClient.post('/hr/paie/soldes/calculer', s)),
  valider:   async (id: string) => unwrap<SoldeToutCompte>(await apiClient.post(`/hr/paie/soldes/${id}/valider`)),
  annuler:   async (id: string, motif: string) => unwrap<SoldeToutCompte>(await apiClient.post(`/hr/paie/soldes/${id}/annuler`, { motif })),
  telechargerPdf: async (id: string, nom: string) => {
    const r = await apiClient.get(`/hr/paie/soldes/${id}/pdf`, { responseType: 'blob' });
    const n = String(r.headers['content-disposition'] ?? '').match(/filename="([^"]+)"/)?.[1] ?? nom;
    const url = URL.createObjectURL(r.data); const a = document.createElement('a');
    a.href = url; a.download = n; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  },
};

// ── PAIE-5 : comptes de paie, écriture du mois, paiement, états CNPS et impôt ──────────────────────
export type PostePaie = 'SALAIRES' | 'CHARGES_SOCIALES' | 'CONTRIB_EMPLOYEUR' | 'TAXE_APPRENTISSAGE' | 'FORMATION_CONTINUE' | 'NET_A_PAYER' | 'RETENUES'
  | 'CNPS_SALARIE' | 'CNPS_EMPLOYEUR' | 'CMU' | 'ETAT_IMPOT_SALARIE' | 'ETAT_CONTRIB_EMPLOYEUR' | 'ETAT_FDFP' | 'BANQUE';
export interface ComptePaie {
  poste: PostePaie; libellePoste: string; compteCode: string | null; libelleCompte: string | null; valideLe: string | null; validePar: string | null;
  compteExistant: boolean; libelleAuPlan: string | null;
}
export interface LigneCnps {
  nom: string; matricule: string; numeroCnps: string | null; piece: string | null; brutCnps: number; baseRetraite: number | null; retraiteSalarie: number;
  retraitePatronale: number; basePf: number | null; prestationsFamiliales: number; maternite: number; accidentTravail: number; cmuSalarie: number; cmuPatronale: number; total: number;
}
export interface LigneImpot {
  nom: string; matricule: string; piece: string | null; brutImposable: number; baseImpot: number | null; parts: number | null; impot: number;
  contributionEmployeur: number; taxeApprentissage: number; formationContinue: number;
}
export interface DeclarationsMois {
  mois: string; statut: 'brouillon' | 'validee'; essai: boolean;
  cnps: { lignes: LigneCnps[]; totaux: Record<string, number>; alertes: string[] };
  impot: { lignes: LigneImpot[]; totaux: Record<string, number>; alertes: string[] };
}
export const paieComptaApi = {
  comptes:        async () => unwrap<ComptePaie[]>(await apiClient.get('/hr/paie/comptes')),
  modifierCompte: async (poste: PostePaie, compteCode: string, libelleCompte: string) => unwrap<ComptePaie[]>(await apiClient.put(`/hr/paie/comptes/${poste}`, { compteCode, libelleCompte })),
  validerComptes: async () => unwrap<ComptePaie[]>(await apiClient.post('/hr/paie/comptes/valider')),
  comptabiliser:  async (mois: string) => unwrap<unknown>(await apiClient.post('/hr/paie/mois/comptabiliser', { mois })),
  payer:          async (mois: string, date: string, reference?: string) => unwrap<unknown>(await apiClient.post('/hr/paie/mois/payer', { mois, date, reference })),
  declarations:   async (mois: string) => unwrap<DeclarationsMois>(await apiClient.get('/hr/paie/mois/declarations', { params: { mois } })),
  telechargerEtat: async (type: 'cnps' | 'impot', mois: string) => {
    const r = await apiClient.get(`/hr/paie/mois/declarations/${type}/pdf`, { params: { mois }, responseType: 'blob' });
    const nom = String(r.headers['content-disposition'] ?? '').match(/filename="([^"]+)"/)?.[1] ?? `etat-${type}-${mois}.pdf`;
    const url = URL.createObjectURL(r.data); const a = document.createElement('a');
    a.href = url; a.download = nom; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  },
};
